/**
 * Signature Audit Trail Utility
 * Captures digital footprint for e-signature compliance (UK legal requirements)
 */

/**
 * Get the user's IP address via a public API
 * Uses ip-api.com for geolocation data
 */
export const getIPAndLocation = async () => {
    try {
        // Use ip-api.com for IP and geolocation (free tier, no API key needed)
        const response = await fetch('http://ip-api.com/json/?fields=status,message,country,countryCode,region,regionName,city,zip,lat,lon,timezone,isp,org,as,query');
        
        if (!response.ok) {
            throw new Error('Failed to fetch IP data');
        }
        
        const data = await response.json();
        
        if (data.status === 'fail') {
            console.warn('IP lookup failed:', data.message);
            return {
                ip_address: null,
                city: null,
                country: null,
                region: null,
                timezone: null,
                isp: null,
                error: data.message
            };
        }
        
        return {
            ip_address: data.query,
            city: data.city,
            country: data.country,
            country_code: data.countryCode,
            region: data.regionName,
            timezone: data.timezone,
            isp: data.isp,
            org: data.org,
            latitude: data.lat,
            longitude: data.lon
        };
    } catch (error) {
        console.error('Error fetching IP/location:', error);
        
        // Fallback: try ipify for just the IP
        try {
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();
            return {
                ip_address: ipData.ip,
                city: null,
                country: null,
                region: null,
                timezone: null,
                isp: null,
                error: 'Geolocation unavailable'
            };
        } catch (fallbackError) {
            return {
                ip_address: null,
                city: null,
                country: null,
                error: 'Unable to determine IP address'
            };
        }
    }
};

/**
 * Get browser and device information from User Agent
 */
export const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const colorDepth = window.screen.colorDepth;
    const devicePixelRatio = window.devicePixelRatio;
    
    // Parse browser info
    let browser = 'Unknown';
    let browserVersion = '';
    
    if (ua.includes('Firefox/')) {
        browser = 'Firefox';
        browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Edg/')) {
        browser = 'Microsoft Edge';
        browserVersion = ua.match(/Edg\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Chrome/')) {
        browser = 'Chrome';
        browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || '';
    } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
        browser = 'Safari';
        browserVersion = ua.match(/Version\/(\d+)/)?.[1] || '';
    } else if (ua.includes('MSIE') || ua.includes('Trident/')) {
        browser = 'Internet Explorer';
        browserVersion = ua.match(/(?:MSIE |rv:)(\d+)/)?.[1] || '';
    }
    
    // Parse OS info
    let os = 'Unknown';
    if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
    else if (ua.includes('Windows NT 6.3')) os = 'Windows 8.1';
    else if (ua.includes('Windows NT 6.2')) os = 'Windows 8';
    else if (ua.includes('Windows NT 6.1')) os = 'Windows 7';
    else if (ua.includes('Mac OS X')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    // Detect device type
    let deviceType = 'Desktop';
    if (/Mobi|Android/i.test(ua)) {
        deviceType = 'Mobile';
    } else if (/iPad|Tablet/i.test(ua)) {
        deviceType = 'Tablet';
    }
    
    return {
        user_agent: ua,
        browser: `${browser}${browserVersion ? ` ${browserVersion}` : ''}`,
        operating_system: os,
        platform,
        device_type: deviceType,
        language,
        screen_resolution: `${screenWidth}x${screenHeight}`,
        color_depth: colorDepth,
        pixel_ratio: devicePixelRatio
    };
};

/**
 * Generate a complete signature audit trail
 * Captures all required data for UK legal compliance
 */
export const generateSignatureAuditTrail = async (signerInfo = {}) => {
    // Get timestamp in ISO 8601 UTC format
    const timestamp = new Date().toISOString();
    
    // Get IP and location data
    const locationData = await getIPAndLocation();
    
    // Get device info
    const deviceInfo = getDeviceInfo();
    
    return {
        // Signer Information
        signer_name: signerInfo.name || null,
        signer_email: signerInfo.email || null,
        signer_id: signerInfo.id || null,
        
        // Timestamp
        signed_at: timestamp,
        signed_at_utc: timestamp,
        timezone_offset: new Date().getTimezoneOffset(),
        
        // IP & Location
        ip_address: locationData.ip_address,
        city: locationData.city,
        country: locationData.country,
        country_code: locationData.country_code,
        region: locationData.region,
        timezone: locationData.timezone,
        isp: locationData.isp,
        coordinates: locationData.latitude && locationData.longitude 
            ? { lat: locationData.latitude, lon: locationData.longitude }
            : null,
        
        // Device & Browser
        user_agent: deviceInfo.user_agent,
        browser: deviceInfo.browser,
        operating_system: deviceInfo.operating_system,
        device_type: deviceInfo.device_type,
        platform: deviceInfo.platform,
        language: deviceInfo.language,
        screen_resolution: deviceInfo.screen_resolution,
        
        // Metadata
        audit_version: '1.0',
        consent_given: true,
        method: 'electronic_signature'
    };
};

/**
 * Format audit trail for display
 */
export const formatAuditTrailForDisplay = (auditTrail) => {
    if (!auditTrail) return [];
    
    const items = [];
    
    if (auditTrail.signer_name) {
        items.push({ label: 'Signer Name', value: auditTrail.signer_name });
    }
    if (auditTrail.signer_email) {
        items.push({ label: 'Signer Email', value: auditTrail.signer_email });
    }
    if (auditTrail.signed_at) {
        items.push({ 
            label: 'Signed At', 
            value: new Date(auditTrail.signed_at).toLocaleString('en-GB', {
                dateStyle: 'full',
                timeStyle: 'long'
            })
        });
    }
    if (auditTrail.ip_address) {
        items.push({ label: 'IP Address', value: auditTrail.ip_address });
    }
    if (auditTrail.city && auditTrail.country) {
        items.push({ label: 'Location', value: `${auditTrail.city}, ${auditTrail.country}` });
    } else if (auditTrail.country) {
        items.push({ label: 'Country', value: auditTrail.country });
    }
    if (auditTrail.browser) {
        items.push({ label: 'Browser', value: auditTrail.browser });
    }
    if (auditTrail.operating_system) {
        items.push({ label: 'Operating System', value: auditTrail.operating_system });
    }
    if (auditTrail.device_type) {
        items.push({ label: 'Device Type', value: auditTrail.device_type });
    }
    
    return items;
};

export default {
    getIPAndLocation,
    getDeviceInfo,
    generateSignatureAuditTrail,
    formatAuditTrailForDisplay
};
